import { createFileRoute, Navigate, Outlet, useLocation } from "@tanstack/react-router";
import { CheckIcon, MessageCircleQuestionIcon } from "lucide-react";

import { FullPageLoader } from "@/components/full-page-loader";
import { Button } from "@/components/ui/button";
import {
  currentOnboardingStep,
  ONBOARDING_TIMELINE,
  STEP_ROUTES,
  STEPS_ORDER,
} from "@/features/onboarding/lib/steps";
import { useCurrentTenant } from "@/features/tenant/api/use-current-tenant";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/onboarding")({
  component: OnboardingLayout,
});

/** Port of onboarding.layout.vue — left-rail 4-step timeline + step redirect guard. */
function OnboardingLayout() {
  const { data: tenant } = useCurrentTenant();
  const location = useLocation();

  if (!tenant) {
    return <FullPageLoader />;
  }

  const currentStep = currentOnboardingStep(tenant);
  const stepRoute = STEP_ROUTES[currentStep];

  // Redirect guard: land on the child route matching the tenant's current step.
  if (stepRoute && location.pathname !== stepRoute) {
    return <Navigate to={stepRoute} replace />;
  }
  if (!stepRoute && location.pathname === "/onboarding") {
    return <Navigate to="/onboarding/domain" replace />;
  }

  return (
    <div className="flex h-screen w-full text-left">
      <div className="relative hidden h-screen w-5/12 shrink-0 border-r border-zinc-200 bg-gradient-to-r from-zinc-50 to-zinc-100 p-24 lg:flex">
        <div className="absolute top-6 left-6">
          <img src="/logo_blue.svg" alt="" className="w-10" />
        </div>

        <div className="flex h-full w-full flex-col place-content-between">
          <div>
            <h2 className="text-base leading-7 text-slate-500 italic">
              Complétez les étapes suivantes pour configurer votre Agence de Voyages avec Upclaim
            </h2>
          </div>

          <div className="py-16">
            <ul className="-mb-8">
              {ONBOARDING_TIMELINE.map((event, eventIndex) => {
                const completed = STEPS_ORDER[event.step] < STEPS_ORDER[currentStep];
                const current = currentStep === event.step;
                const StepIcon = completed ? CheckIcon : event.icon;
                return (
                  <li key={event.step}>
                    <div className="relative pb-20">
                      {eventIndex !== ONBOARDING_TIMELINE.length - 1 ? (
                        <span
                          className="absolute top-4 left-6 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="relative flex space-x-6">
                        <span
                          className={cn(
                            "flex size-12 items-center justify-center rounded-full border-4 shadow-lg ring-8 ring-white",
                            current && "bg-blue-500",
                            completed && "bg-green-500",
                            !current && !completed && "bg-slate-300",
                          )}
                        >
                          <StepIcon className="size-7 text-white" aria-hidden="true" />
                        </span>
                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-2.5 pl-1">
                          <p className="text-base text-gray-700">
                            {event.content} <span className="font-medium text-gray-900">{event.target}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mx-auto">
            <Button type="button" size="lg">
              <MessageCircleQuestionIcon className="size-4" aria-hidden="true" />
              Besoin d'aide ?
            </Button>
          </div>
        </div>
      </div>

      <main className="relative z-0 flex flex-1 overflow-y-auto p-24">
        <div>
          <span className="mb-8 inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-700/10 ring-inset">
            Étape {STEPS_ORDER[currentStep] + 1} sur 4
          </span>
          {tenant.tenantBranding?.postmarkappDomainId ? (
            <span className="ml-4 inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset">
              <svg className="size-1.5 fill-blue-500" viewBox="0 0 6 6" aria-hidden="true">
                <circle cx="3" cy="3" r="3" />
              </svg>
              Domaine : <span className="font-semibold">{tenant.tenantBranding?.subdomainUri}</span>
            </span>
          ) : null}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
